import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Key, UserCog } from 'lucide-react';

const UserManagement = () => {
  const { hasRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'MUNIM',
    password: ''
  });

  const canManage = hasRole(['OWNER']);

  useEffect(() => {
    if (canManage) {
      fetchUsers();
    } else {
      setLoading(false);
    }
  }, [canManage]);

  const fetchUsers = async () => {
    try {
      const response = await api.users.list();
      setUsers(response.data);
    } catch (error) {
      if (error?.response?.status === 403) {
        toast.error('Only OWNER can access User Management');
      } else {
        toast.error('Failed to fetch users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.users.update(editingUser.id, {
          full_name: formData.full_name,
          role: formData.role,
          is_active: true
        });
        toast.success('User updated successfully');
      } else {
        await api.users.create(formData);
        toast.success('User created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      await api.users.resetPassword(selectedUser.id, newPassword);
      toast.success('Password reset successfully');
      setPasswordDialogOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      password: ''
    });
    setDialogOpen(true);
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      full_name: '',
      role: 'MUNIM',
      password: ''
    });
  };

  if (!canManage) {
    return (
      <div className="space-y-6 bg-[#141414] p-6 text-white min-h-screen" data-testid="user-management-page">
        <div className="border-b border-[#333333] pb-4">
          <h1 className="font-mono text-3xl font-bold tracking-wider text-white uppercase">User Management</h1>
        </div>
        <div className="border-l-4 border-[#EF4444] bg-[#1E1E1E] p-4 text-xs font-mono text-slate-300 rounded-none border border-y-[#333333] border-r-[#333333]">
          <span className="font-bold text-[#EF4444] uppercase tracking-wider block mb-1">Access Denied</span>
          Only system owner accounts are authorized to access the system user management registry.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-[#141414] p-6 text-white min-h-screen" data-testid="user-management-page">
      <div className="flex items-center justify-between border-b border-[#333333] pb-4">
        <div>
          <h1 className="font-mono text-3xl font-bold tracking-wider text-white uppercase">User Management</h1>
          <p className="text-slate-400 font-sans text-xs tracking-wider uppercase mt-1">Manage system users and roles</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button
              onClick={resetForm}
              data-testid="create-user-button"
              className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-4 flex items-center justify-center gap-1.5 transition-colors"
            >
              <Plus className="h-4 w-4" />
              New User
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-[#1E1E1E] border border-[#333333] rounded-none text-white">
            <DialogHeader className="border-b border-[#333333] pb-3">
              <DialogTitle className="font-mono text-lg uppercase tracking-wider text-white">{editingUser ? 'Edit User Credentials' : 'Register New User'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div>
                <label htmlFor="email" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Email *</label>
                <input
                  id="email"
                  type="email"
                  data-testid="user-email-input"
                  className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm disabled:opacity-30 disabled:cursor-not-allowed"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!!editingUser}
                  required
                />
              </div>
              <div>
                <label htmlFor="full_name" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Full Name *</label>
                <input
                  id="full_name"
                  data-testid="user-name-input"
                  className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Role *</label>
                <select
                  className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  data-testid="user-role-select"
                >
                  <option value="ADMIN">Admin</option>
                  <option value="MUNIM">Munim</option>
                </select>
              </div>
              {!editingUser && (
                <div>
                  <label htmlFor="password" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Password *</label>
                  <input
                    id="password"
                    type="password"
                    data-testid="user-password-input"
                    className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                  />
                </div>
              )}
              <div className="flex gap-2 justify-end pt-4 border-t border-[#333333]">
                <button
                  type="button"
                  className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="user-submit-button"
                  className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-6 flex items-center justify-center gap-1.5 transition-colors"
                >
                  {editingUser ? 'Update User' : 'Register User'}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-[#1E1E1E] border border-[#333333] rounded-none">
        <div className="p-4 border-b border-[#333333] bg-[#2A2A2A]/40 flex items-center gap-2">
          <UserCog className="h-4 w-4 text-[#FF6B00]" />
          <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-white">System Users Registry</h2>
        </div>
        <div className="p-4">
          {loading ? (
            <div className="text-center py-8 font-mono text-xs text-slate-400">LOADING USERS DIRECTORY...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[#333333] bg-[#2A2A2A]/20">
                    <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400">Name</th>
                    <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400">Email</th>
                    <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400">Role</th>
                    <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400">Status</th>
                    <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#333333]/30">
                  {users.map((user, idx) => (
                    <tr
                      key={user.id}
                      data-testid={`user-row-${user.id}`}
                      className={idx % 2 === 0 ? 'bg-[#1E1E1E]' : 'bg-[#1E1E1E]/50'}
                    >
                      <td className="px-4 py-2 text-xs font-sans text-white font-medium">{user.full_name}</td>
                      <td className="px-4 py-2 text-xs font-mono text-slate-300">{user.email}</td>
                      <td className="px-4 py-2 text-xs">
                        <span className={`font-mono text-[9px] border px-2 py-0.5 uppercase tracking-wider ${
                          user.role === 'OWNER' ? 'bg-purple-950/40 border-purple-900 text-purple-400' :
                          user.role === 'ADMIN' ? 'bg-red-950/40 border-red-900 text-red-400' :
                          user.role === 'MUNIM' ? 'bg-blue-950/40 border-blue-900 text-blue-400' :
                          'bg-green-950/40 border-green-900 text-green-400'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {user.is_active ? (
                          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-[#10B981]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] shadow-[0_0_6px_#10B981] inline-block" /> ACTIVE
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-[#6B7280]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#6B7280] shadow-[0_0_6px_#6B7280] inline-block" /> INACTIVE
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-1.5">
                          <button
                            type="button"
                            className="p-1.5 bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            data-testid={`edit-user-${user.id}`}
                            disabled={user.role === 'OWNER'}
                            onClick={() => openEditDialog(user)}
                            title="Edit User"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            className="p-1.5 bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            data-testid={`reset-password-${user.id}`}
                            disabled={user.role === 'OWNER'}
                            onClick={() => openPasswordDialog(user)}
                            title="Reset Password"
                          >
                            <Key className="h-3.5 w-3.5 text-amber-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-md bg-[#1E1E1E] border border-[#333333] rounded-none text-white">
          <DialogHeader className="border-b border-[#333333] pb-3">
            <DialogTitle className="font-mono text-lg uppercase tracking-wider text-white">Reset Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordReset} className="space-y-4 pt-4">
            <p className="text-xs text-slate-400 font-mono">
              RESETTING PASSWORD FOR: <span className="font-semibold text-white">{selectedUser?.full_name}</span>
            </p>
            <div>
              <label htmlFor="new_password" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">New Password *</label>
              <input
                id="new_password"
                type="password"
                data-testid="new-password-input"
                className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div className="flex gap-2 justify-end pt-4 border-t border-[#333333]">
              <button
                type="button"
                className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                onClick={() => setPasswordDialogOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="confirm-password-reset"
                className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-6 flex items-center justify-center gap-1.5 transition-colors"
              >
                Reset Password
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
